import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { Button } from '../../components/ui/Button';
import { Checkbox } from '../../components/ui/Checkbox';
import { OtpInput } from '../../components/ui/OtpInput';
import { api, ApiError } from '../../lib/api';
import { setDeviceToken, setTokens } from '../../lib/tokens';

const methodLabels: Record<string, string> = {
  totp: 'Приложение',
  sms: 'SMS',
  email: 'E-mail',
  telegram: 'Telegram',
};

type MfaLocationState = {
  challengeId: string;
  availableFactors?: string[];
};

export function MfaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as MfaLocationState | null;

  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!state?.challengeId) {
      navigate('/login', { replace: true });
    }
  }, [state, navigate]);

  if (!state?.challengeId) return null;

  const activeFactor = state.availableFactors?.[0] ?? 'email';

  const handleConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      const response = await api.mfaVerify({
        challenge_id: state.challengeId,
        code,
        trust_device: trustDevice,
      });
      setTokens(response.tokens.access_token, response.tokens.refresh_token);
      if (response.device_token) setDeviceToken(response.device_token);
      navigate('/workspace');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось подтвердить вход.');
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout>
      <div className="mx-auto flex w-[420px] max-w-full flex-col gap-6">
        <Link to="/login" className="text-xs font-medium text-action-primary">
          ← Отменить вход
        </Link>

        <div>
          <div className="flex size-12 items-center justify-center rounded-[14px] bg-status-warning text-lg text-text-inverse">
            ✓
          </div>
          <h1 className="mt-4 text-[28px] font-semibold text-text-primary">Подтвердите вход</h1>
          <p className="mt-2 text-[13px] text-text-secondary">
            Это новое устройство. Введите код второго фактора.
          </p>
        </div>

        <div className="flex h-10 items-center rounded-[10px] bg-bg-surface-secondary px-4">
          <p className="text-[11px] font-semibold text-action-primary">
            Код отправлен через: {methodLabels[activeFactor] ?? activeFactor}
          </p>
        </div>

        <div>
          <p className="text-[11px] font-medium text-text-secondary">Код подтверждения</p>
          <div className="mt-3">
            <OtpInput value={code} onChange={setCode} />
          </div>
        </div>

        {error && <p className="text-xs font-medium text-status-warning">{error}</p>}

        <Checkbox
          label="Доверять этому устройству 30 дней"
          checked={trustDevice}
          onChange={(e) => setTrustDevice(e.target.checked)}
        />

        <Button
          type="button"
          className="w-full"
          disabled={code.length < 6 || submitting}
          onClick={handleConfirm}
        >
          {submitting ? 'Проверяем…' : 'Подтвердить вход'}
        </Button>

        <div className="rounded-xl bg-bg-surface-secondary p-4">
          <p className="text-[11px] leading-relaxed text-text-secondary">
            <span className="font-semibold text-action-primary">i</span> Коды второго фактора никогда
            не запрашиваются службой поддержки.
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
